/**
 * The cosmetic contact-lens colours this shop sells. One list, owned here.
 *
 * WHY THIS IS A FIXED LIST AND THE FRAME PALETTE IS NOT
 * ----------------------------------------------------
 * A frame colour is shop data: a merchant who stocks a frame in a shade nobody
 * anticipated adds it once through Add New Color and it is there forever. That
 * is why `catalog/colors.ts` holds only matching rules and asks the backend for
 * the palette itself.
 *
 * A lens colour is not shop data. The phone's lens filter owns its own fixed
 * vocabulary and does not ask the backend for it, so a lens published under a
 * key no chip matches would be invisible under every filter including the
 * all-restoring one — which is why the backend makes it a publish blocker
 * (APPROVED_LENS_COLOR_KEYS in app/services/product_family.py) rather than a
 * warning. A free-text box here would therefore be a box whose wrong answers
 * are only discovered at publish time, so there is no box: there are twelve
 * choices, and they are these twelve.
 *
 * THE KEY IS NOT TYPED, IT IS DERIVED
 * -----------------------------------
 * The backend derives `normalized_color_key` from the name it is sent
 * (`normalize_color_key` -> slugify). Every display name below slugifies to
 * exactly its own key, which is asserted in this module's tests — so sending
 * the name is sending the key, and the two cannot drift apart by a typo.
 */

/** One approved lens colour. */
export interface LensColor {
  /** The normalized_color_key the product contract stores, and the phone filters by. */
  key: string;
  /** What the administrator and the customer both read. */
  name: string;
  swatch_hex: string;
}

/**
 * The twelve, in the order they are offered.
 *
 * The count is asserted in the test suite: a thirteenth entry here would offer
 * a colour the backend refuses at publish, and a missing one would make a
 * colour the shop really sells unreachable.
 *
 * The order and the swatches are the phone's own — `kLensColorTaxonomy` in
 * lib/models/lens_color_taxonomy.dart — so an administrator picking a shade
 * sees the shade the shopper will see, in the position the shopper meets it.
 * A shade sits beside the shade it is a variation of rather than at the end.
 */
export const LENS_COLORS: readonly LensColor[] = [
  { key: "ash-grey", name: "Ash Grey", swatch_hex: "#8C9098" },
  { key: "sterling-gray", name: "Sterling Gray", swatch_hex: "#B4BAC2" },
  { key: "charcoal-gray", name: "Charcoal Gray", swatch_hex: "#4A4F57" },
  { key: "hazel", name: "Hazel", swatch_hex: "#A87A3D" },
  { key: "honey-brown", name: "Honey Brown", swatch_hex: "#C08A3E" },
  { key: "brown", name: "Brown", swatch_hex: "#8B5E3C" },
  { key: "ice-blue", name: "Ice Blue", swatch_hex: "#7FA8C4" },
  { key: "true-sapphire", name: "True Sapphire", swatch_hex: "#2E5C9A" },
  { key: "turquoise", name: "Turquoise", swatch_hex: "#45A09B" },
  { key: "olive-green", name: "Olive Green", swatch_hex: "#6E7A45" },
  { key: "gemstone-green", name: "Gemstone Green", swatch_hex: "#2F8F5B" },
  { key: "violet", name: "Violet", swatch_hex: "#7C6BA8" },
] as const;

export const LENS_COLOR_KEYS: readonly string[] = LENS_COLORS.map((c) => c.key);

/** The approved colour with this key, or null. Null is a refusal, not a
 *  default: substituting one would put a colour on a storefront nobody chose. */
export function lensColorForKey(key: string | null | undefined): LensColor | null {
  if (!key) return null;
  const wanted = key.trim().toLowerCase();
  return LENS_COLORS.find((color) => color.key === wanted) ?? null;
}

export function isApprovedLensColorKey(key: string | null | undefined): boolean {
  return lensColorForKey(key) !== null;
}

/**
 * How many enabled colours a publishable product of this family may have.
 *
 * ONE cosmetic lens product is ONE colour — Ash Grey and Hazel are two
 * products, not two settings of one. Mirrors MAX_ENABLED_VARIANTS in
 * app/services/product_family.py; the backend refuses a second at publish and
 * this is what stops the dashboard from offering one in the first place.
 */
export const MAX_ENABLED_LENS_VARIANTS = 1;
